import Dispatcher from "Dispatcher";
import PropTypes from "prop-types";
import ContextedReduceStore from "data/util/ContextedReduceStore";
import SuperRecordActions from "data/SuperRecordActions";
import TableStore from "data/TableStore";
import LockApi from "data/LockApi";
import LockActions from "data/LockActions";
import TableAccess from "data/util/TableAccess";
import LockBuilder from "data/LockBuilder";
import ClientId from "data/ClientId";
import ContextActions from "data/util/ContextActions";
import SuperRecordStore from "data/SuperRecordStore";
import typeCheckingStore from "data/util/typeCheckingStore";
import { recordIdType } from "data/util/typedefs";
import MergeActions from "./MergeActions";
import MergeStore, { MERGE_PLUGINS } from "./MergeStore";

export const LockTypes = {
    LAYER: "layer", // normal kind
    MERGE_SOURCE: "merge-source",
};

const resetLockStates = (context, optViolators, pessViolators) => {
    const optlock = optViolators && optViolators.length
        ? {
            ...context.optlock,
            violated: true,
            violators: optViolators,
        }
        : {
            ...context.optlock,
            violated: false,
            violators: null,
            dismissed: false,
        };
    const pesslock = pessViolators && pessViolators.length
        ? {
            ...context.pesslock,
            violated: true,
            violators: pessViolators.map(v => v.username),
        }
        : {
            ...context.pesslock,
            violated: false,
            violators: null,
            dismissed: false,
        };
    return {
        ...context,
        optlock,
        pesslock,
    };
};

const releaseLockSetForContext = context => {
    context = resetLockStates(context);
    if (!context.lockSet) return context;
    if (ClientId.is(context.lockSet.id)) {
        // no need to go server-side
        return deleteLockSetFromContext(context);
    }
    LockApi.releaseLockset(context.lockSet);
    return context;
};

const deleteLockSetFromContext = context => {
    context = {
        ...context,
    };
    delete context.lockSet;
    return context;
};

const createLockSetForContext = (context, key, id, type=LockTypes.LAYER) => {
    context = releaseLockSetForContext(context);
    const builder = new LockBuilder(
        new TableAccess(TableStore.getState(), true),
        key,
        id,
    );
    if (builder.isEmpty()) {
        return context;
    }
    const lockSet = builder.toLockSet();
    LockApi.createLockSet(lockSet);
    return {
        ...context,
        key,
        id,
        type,
        lockSet,
    };
};

const processSuperRecordUpdateState = (context, action) => {
    if(
        MergeStore.getPluginKey() === MERGE_PLUGINS.GLOBAL_MERGE
    ) {
        const mergeState = MergeStore.getState();
        if(
            mergeState.source.stereoKey === context.key
            && mergeState.source.id === context.id
        ) {
            // remove the lock state for the merge source
            return releaseLockSetForContext(context);
        }
    }

    if (context.key !== action.key || context.id !== action.id) {
        return context;
    }
    return createLockSetForContext(context, context.key, context.id);
};

export class LockStore extends ContextedReduceStore {

    getInitialContext() {
        return resetLockStates({});
    }

    reduceContext(context, action) {
        switch (action.type) {

            case SuperRecordActions.NEW: {
                return {
                    ...releaseLockSetForContext(context),
                    key: action.stereoKey,
                    id: action.newId,
                };
            }

            case ContextActions.CLOSE: {
                return releaseLockSetForContext(context);
            }

            case SuperRecordActions.CANCEL:
            case SuperRecordActions.CANCEL_FOR_OPTLOCK: {
                return {
                    ...context,
                    optlock: {
                        ...context.optlock,
                        dismissed: true,
                    },
                    pesslock: {
                        ...context.pesslock,
                        dismissed: true,
                    },
                };
            }

            case SuperRecordActions.OPEN: {
                this.__dispatcher.waitFor([
                    TableStore.getDispatchToken(),
                    SuperRecordStore.getDispatchToken(),
                ]);
                return createLockSetForContext(context, action.key, action.id);
            }

            case MergeActions.SELECT_SOURCE: {
                this.__dispatcher.waitFor([
                    MergeStore.getDispatchToken(),
                ]);
                if(
                    MergeStore.isReady()
                    && MergeStore.getPluginKey() === MERGE_PLUGINS.GLOBAL_MERGE
                ) {
                    // only for global merge
                    return createLockSetForContext(context, action.source.stereoKey, action.source.id, LockTypes.MERGE_SOURCE);
                }
                return context;
            }

            case MergeActions.CANCEL:
            case MergeActions.CANCEL_FOR_SOURCE_OPTLOCK: {
                if(
                    MergeStore.getPluginKey() === MERGE_PLUGINS.GLOBAL_MERGE
                    && MergeStore.getState().source !== null
                ) {
                    // global merge with selected source canceled.
                    return releaseLockSetForContext(context);
                }
                return context;
            }

            default:
                return context;
        }
    }

    reduceEachContext(context, action, contextId) {
        switch (action.type) {

            case SuperRecordActions.CREATED: {
                if (context.key !== action.key || context.id !== action.id) {
                    return context;
                }
                this.__dispatcher.waitFor([
                    TableStore.getDispatchToken(),
                    SuperRecordStore.getDispatchToken(),
                ]);
                context = {
                    ...context,
                    id: action.data.id,
                };
                return createLockSetForContext(
                    context,
                    context.key,
                    context.id,
                );
            }

            case SuperRecordActions.UPDATED: {
                this.__dispatcher.waitFor([
                    TableStore.getDispatchToken(),
                    SuperRecordStore.getDispatchToken(),
                ]);
                return processSuperRecordUpdateState(context, action);
            }

            case SuperRecordActions.DELETED: {
                if (context.key !== action.key || context.id !== action.id) {
                    return context;
                }
                this.__dispatcher.waitFor([
                    TableStore.getDispatchToken(),
                    SuperRecordStore.getDispatchToken(),
                ]);
                return releaseLockSetForContext(context);
            }

            case LockActions.LOCKSET_CREATED:
            case LockActions.LOCKSET_CONFIRMED: {
                if (!context.lockSet || action.id !== context.lockSet.id) {
                    return context;
                }
                context = {
                    ...context,
                    lockSet: action.data,
                };
                context = resetLockStates(context, action.data.optimisticViolators, action.data.pessimisticViolators);
                if (context.optlock.violated) {
                    LockApi.releaseLockset(context.lockSet);
                }
                return context;
            }

            case LockActions.LOCKSET_RELEASED: {
                if (!context.lockSet || action.id !== context.lockSet.id) {
                    return context;
                }
                return deleteLockSetFromContext(context);
            }

            default:
                return context;
        }
    }

    getOptLock(context) {
        return this.getState(context).optlock;
    }

    getPessLock(context) {
        const s = this.getState(context);
        return s.lockSet && !ClientId.is(s.lockSet.id) ? s.pesslock : null;
    }

    getActiveLocksetIds() {
        return this.getEachContext()
            .map(ctx => ctx.lockSet)
            .filter(ls => ls)
            .map(ls => ls.id)
            .filter(id => id && !ClientId.is(id));
    }

}

export const lockShape = {
    violated: PropTypes.bool.isRequired,
    violators: PropTypes.arrayOf(PropTypes.string),
    dismissed: PropTypes.bool.isRequired,
};

LockStore.contextTypes = {
    key: PropTypes.string,
    id: recordIdType,
    type: PropTypes.string,
    lockSet: PropTypes.object,
    optlock: PropTypes.exact(lockShape).isRequired,
    pesslock: PropTypes.exact(lockShape).isRequired,
};

const lockStore = typeCheckingStore(new LockStore(Dispatcher));

export default lockStore;



// WEBPACK FOOTER //
// ./app/data/lockStore.js